import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { getOperators } from '../utils/storage';

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('supplier');
  const [newCompany, setNewCompany] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [usersRes, opsRes] = await Promise.all([
      supabase.from('bc_users').select('*').order('created_at', { ascending: false }),
      getOperators(),
    ]);
    setUsers(usersRes.data || []);
    setOperators(opsRes);
    setLoading(false);
  };

  const createUser = async () => {
    if (!newEmail || !newPassword) { setMessage('Email and password required'); return; }
    setCreating(true);
    setMessage('');

    try {
      // Create auth account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newEmail,
        password: newPassword,
        options: { data: { role: newRole, company: newCompany } },
      });
      if (authError) throw authError;

      // Create bc_users record
      const { error: dbError } = await supabase.from('bc_users').insert({
        email: newEmail,
        role: newRole,
        company_name: newCompany,
        created_by: 'admin',
      });
      if (dbError && !dbError.message.includes('duplicate')) throw dbError;

      setMessage(`Created ${newRole} account: ${newEmail}`);
      setNewEmail('');
      setNewPassword('');
      setNewCompany('');
      loadData();
    } catch (e) {
      setMessage(`Error: ${e.message}`);
    }
    setCreating(false);
  };

  const updateRole = async (userId, email, newRole) => {
    await supabase.from('bc_users').update({ role: newRole }).eq('id', userId);
    setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const updateOperatorAccess = async (userId, operatorIds) => {
    await supabase.from('bc_users').update({ operator_ids: operatorIds }).eq('id', userId);
    setUsers(users.map(u => u.id === userId ? { ...u, operator_ids: operatorIds } : u));
  };

  const deleteUser = async (userId) => {
    if (!confirm('Delete this user account?')) return;
    await supabase.from('bc_users').delete().eq('id', userId);
    setUsers(users.filter(u => u.id !== userId));
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pw = '';
    for (let i = 0; i < 12; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setNewPassword(pw);
  };

  return (
    <div className="py-6">
      <h1 className="font-display text-2xl text-white mb-2">Admin Panel</h1>
      <p className="text-xs text-neutral-500 mb-6">Manage supplier and operator accounts</p>

      {/* Create User */}
      <div className="card p-5 mb-6">
        <h2 className="text-sm font-semibold text-brand-gold mb-4">Create New Account</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="form-group">
            <label className="text-[11px] text-neutral-500 mb-1">Email</label>
            <input className="input-field text-sm py-2" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@company.com" />
          </div>
          <div className="form-group">
            <label className="text-[11px] text-neutral-500 mb-1">Password</label>
            <div className="flex gap-1">
              <input className="input-field text-sm py-2 flex-1" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="min 6 chars" />
              <button onClick={generatePassword} className="text-[9px] px-2 rounded bg-neutral-800 text-neutral-500 hover:text-brand-gold transition-colors" title="Generate password">Gen</button>
            </div>
          </div>
          <div className="form-group">
            <label className="text-[11px] text-neutral-500 mb-1">Role</label>
            <select className="input-field text-sm py-2" value={newRole} onChange={e => setNewRole(e.target.value)}>
              <option value="supplier">Supplier</option>
              <option value="operator">Operator</option>
            </select>
          </div>
          <div className="form-group">
            <label className="text-[11px] text-neutral-500 mb-1">Company Name</label>
            <input className="input-field text-sm py-2" value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="e.g., Campari Group" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={createUser} disabled={creating} className="btn-primary text-xs py-2 disabled:opacity-50">
            {creating ? 'Creating...' : 'Create Account'}
          </button>
          {message && <span className={`text-xs ${message.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>{message}</span>}
        </div>
      </div>

      {/* User List */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-brand-gold mb-4">All External Accounts ({users.length})</h2>
        {loading ? (
          <p className="text-neutral-500 text-sm text-center py-4">Loading...</p>
        ) : users.length === 0 ? (
          <p className="text-neutral-600 text-sm text-center py-4">No external accounts yet. Create one above.</p>
        ) : (
          <div className="space-y-3">
            {users.map(user => (
              <div key={user.id} className="bg-neutral-800/20 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{user.email}</p>
                    <p className="text-[10px] text-neutral-500">{user.company_name || 'No company'} · Created {new Date(user.created_at).toLocaleDateString()}</p>
                  </div>
                  <select
                    value={user.role}
                    onChange={e => updateRole(user.id, user.email, e.target.value)}
                    className={`text-[10px] px-2 py-1 rounded font-medium border-0 ${
                      user.role === 'supplier' ? 'bg-blue-500/15 text-blue-400' :
                      user.role === 'operator' ? 'bg-emerald-500/15 text-emerald-400' :
                      'bg-neutral-800 text-neutral-400'
                    }`}
                  >
                    <option value="supplier">Supplier</option>
                    <option value="operator">Operator</option>
                  </select>
                  <button onClick={() => deleteUser(user.id)} className="text-[10px] text-neutral-700 hover:text-red-400">Delete</button>
                </div>

                {/* Operator access */}
                {operators.length > 0 && (
                  <div className="mt-2">
                    <p className="text-[9px] text-neutral-600 mb-1">
                      {user.role === 'supplier' ? 'Can see these operators:' : 'Linked to:'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {operators.map(op => {
                        const hasAccess = (user.operator_ids || []).includes(op.id);
                        return (
                          <button
                            key={op.id}
                            onClick={() => {
                              const current = user.operator_ids || [];
                              const updated = hasAccess ? current.filter(id => id !== op.id) : [...current, op.id];
                              updateOperatorAccess(user.id, updated);
                            }}
                            className={`text-[9px] px-2 py-1 rounded transition-all ${
                              hasAccess ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/30' : 'bg-neutral-800/50 text-neutral-600 border border-neutral-800 hover:text-neutral-400'
                            }`}
                          >
                            {op.operator_name || 'Untitled'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
